import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import PropTypes from 'prop-types';
import {Collapse} from 'react-collapse';
import {useSelector} from 'react-redux';
import {InfoCircleOutlined} from '@ant-design/icons';
import {Alert, Button, Form, Input, Select} from 'antd';
import {StyledEditBudgetModal} from './StyledEditBudgetModal';
import EuroInput from '../../../EuroInput';
import SpinSmall from '../../../SpinSmall';
import UserMultiSelect from '../../../Select/UserMultiSelect';
import {formHelpers} from '../../../../utils/helpers';
import {colorsStyles} from '../../../../utils/styles';
import {useFormHelpers} from '../../../../hooks';

const {Item} = Form;

const employeeIdKey = 'email';

const defaultFormValues = {
  collaborators: [],
  cost_centers: undefined,
  expense_category: undefined,
  limit_amount: undefined,
  name: '',
  reported_by: undefined
};

const EditBudgetModal = ({
  budget,
  handleOk,
  handleCancel,
  loading = false,
  open,
  ...rest
}) => {
  const [t] = useTranslation(['main', 'budgets']);
  const [form] = Form.useForm();
  const [error, setError] = useState(null);
  const [initialFormValues, setInitialFormValues] = useState(defaultFormValues);

  const {costCenterTags, expenseTags} = useSelector(state => ({
    costCenterTags: state?.company?.costCenterTags || [],
    expenseTags: state?.company?.expenseTags || [],
  }));

  const {employeeOptions, placeholderEnter, placeholderSelect} = useFormHelpers({employeeIdKey});

  const expenseCategoryOptions = useMemo(() => formHelpers.getTagOptions(expenseTags), [expenseTags]);

  const costCenterOptions = useMemo(() => formHelpers.getTagOptions(costCenterTags), [costCenterTags]);

  const tooltipTitle = (key) => t(`budgets:modal.addCollaborators.tooltip.${key}`);

  useEffect(() => {
    if (open && budget) {
      const initialValues = {
        collaborators: (budget?.collaborators || []).map(c => c.email),
        cost_centers: budget?.cost_centers?.[0] || undefined,
        expense_category: budget?.expense_category || undefined,
        limit_amount: budget?.limit_amount || 0,
        name: budget?.name || '',
        reported_by: budget?.reported_by?.email || undefined,
      }
      setInitialFormValues(initialValues);
      form.setFieldsValue(initialValues);
    }
  }, [form, open, budget]);

  const handleAfterOpenChange = (open) => {
    if (!open) {
      form.setFieldsValue(defaultFormValues);
      if (error) setError(null);
    }
  }

  const handleSubmit = ({limit_amount, ...data}) => {
    handleOk?.({
      data,
      errorCallback: (response) => formHelpers.handleFormErrors({form, t, setError, response})
    });
  }

  const rules = [{required: true, message: t('validation.fieldIsRequired')}];

  const footer = [
    <Button
      key='next'
      disabled={loading}
      onClick={() => form.submit()}
      size='large'
      type='primary'
    >
      {t('edit')}
    </Button>
  ];

  return (
    <StyledEditBudgetModal
      afterOpenChange={handleAfterOpenChange}
      footer={footer}
      forceRender
      onClose={handleCancel}
      open={open}
      title={t('edit')}
      width={480}
      {...rest}
    >
      <SpinSmall spinning={loading}>
        <Form
          initialValues={initialFormValues}
          form={form}
          layout='vertical'
          onFinish={handleSubmit}
          requiredMark={false}
        >
          <Item
            label={`${t('Budget')} ${t('name')}`}
            name='name'
            required
            rules={rules}
          >
            <Input placeholder={placeholderEnter('name')} size='large' />
          </Item>
          <Item
            label={`${t('Budget')} ${t('Owner')}`}
            name='reported_by'
            tooltip={{
              title: tooltipTitle('budgetOwner'),
              icon: <InfoCircleOutlined style={{ color: colorsStyles.neutral_6 }} />
            }}
            required
            rules={rules}
          >
            <Select
              optionFilterProp='optionLabel'
              options={employeeOptions}
              placeholder={placeholderSelect('user')}
              size='large'
              showSearch
            />
          </Item>
          <Item label={`${t('Budget')} ${t('limit')}`} name='limit_amount'>
            <EuroInput placeholder={placeholderEnter('limit')} disabled />
          </Item>
          <Item label={t('costCenter')} name='cost_centers'>
            <Select
              options={costCenterOptions}
              placeholder={placeholderSelect('team')}
              size='large'
              showSearch
            />
          </Item>
          <Item label={`${t('expense')} ${t('category')}`} name='expense_category'>
            <Select
              options={expenseCategoryOptions}
              placeholder={placeholderSelect('category')}
              size='large'
              showSearch
            />
          </Item>
          <Item label={t('collaborators')} name='collaborators'>
            <UserMultiSelect
              employeeIdKey={employeeIdKey}
              excludeEmployeeEmail={true}
              selectProps={{ placeholder: placeholderSelect('users'), size: 'large' }}
            />
          </Item>
        </Form>
        <Collapse isOpened={Boolean(error)}>
          <Alert message={error} type='error' showIcon />
        </Collapse>
      </SpinSmall>
    </StyledEditBudgetModal>
  );
}

EditBudgetModal.propTypes = {
  budget: PropTypes.shape({
    collaborators: PropTypes.array,
    id: PropTypes.string,
    name: PropTypes.string,
    reported_by: PropTypes.object,
    cost_centers: PropTypes.array,
    limit_amount: PropTypes.number,
  }),
  handleCancel: PropTypes.func,
  handleOk: PropTypes.func,
  loading: PropTypes.bool,
  open: PropTypes.bool.isRequired,
}

export default EditBudgetModal;
